import type { CurrentUser, Locale } from '@mfos/shared';
import { useQueryClient } from '@tanstack/react-query';
import { Fragment, type ReactNode, useEffect, useMemo, useState } from 'react';
import { authKeys, useUpdateProfile } from '../../features/auth/auth.api';
import { setFormatLocale } from '../format';
import {
  I18nContext,
  LOCALES,
  LOCALE_STORAGE_KEY,
  readStoredLocale,
  setActiveLocale,
  translate,
  useI18n,
} from './runtime';
import type { MessageKey } from './messages';

function documentLanguage(locale: Locale): string {
  if (locale === 'HY') return 'hy';
  if (locale === 'RU') return 'ru';
  return 'en';
}

/** Live app wrapper. Tests render without it and keep the English fallback. */
export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale() ?? 'HY');
  setActiveLocale(locale);

  useEffect(() => {
    document.documentElement.lang = documentLanguage(locale);
    setFormatLocale(locale);
    try {
      localStorage.setItem(LOCALE_STORAGE_KEY, locale);
    } catch {
      // Private mode can reject storage; the in-memory choice still applies.
    }
  }, [locale]);

  const value = useMemo(
    () => ({
      locale,
      setLocale: setLocaleState,
      t: (key: MessageKey) => translate(locale, key),
    }),
    [locale],
  );

  // Static text reads the locale through phrase(), so the tree remounts when the language changes.
  return (
    <I18nContext.Provider value={value}>
      <Fragment key={locale}>{children}</Fragment>
    </I18nContext.Provider>
  );
}

/** Header control. Persists on this browser, and on the signed-in profile when one is cached. */
export function LanguageSwitcher() {
  const { locale, setLocale, t } = useI18n();
  const queryClient = useQueryClient();
  const update = useUpdateProfile();

  return (
    <label className="language-switcher">
      <span className="visually-hidden">{t('language')}</span>
      <select
        value={locale}
        aria-label={t('language')}
        onChange={(event) => {
          const next = LOCALES.find((item) => item === event.target.value);
          if (next === undefined || next === locale) return;
          setLocale(next);
          const user = queryClient.getQueryData<CurrentUser>(authKeys.me);
          if (user !== undefined && user.locale !== next) {
            update.mutate({ locale: next });
          }
        }}
      >
        <option value="HY">Հայերեն</option>
        <option value="RU">Русский</option>
        <option value="EN">English</option>
      </select>
    </label>
  );
}
